import { cn } from '@/lib/utils';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

interface SelectProps {
	children: React.ReactNode;
	value?: string;
	defaultValue?: string;
	onValueChange?: (value: string) => void;
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	disabled?: boolean;
	className?: string;
}

const SelectContext = React.createContext<{
	value: string;
	onValueChange: (value: string) => void;
	open: boolean;
	setOpen: (open: boolean) => void;
	triggerRef: React.RefObject<HTMLDivElement | null>;
	disabled: boolean;
} | null>(null);

const Select = ({
	children,
	value,
	defaultValue,
	onValueChange,
	open,
	defaultOpen = false,
	onOpenChange,
	disabled,
	className,
}: SelectProps) => {
	const [uncontrolledValue, setUncontrolledValue] = React.useState(
		defaultValue || '',
	);
	const [isOpen, setIsOpen] = React.useState(defaultOpen);
	const triggerRef = React.useRef<HTMLDivElement | null>(null);

	const handleValueChange = (newValue: string) => {
		if (onValueChange) {
			onValueChange(newValue);
		} else {
			setUncontrolledValue(newValue);
		}
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (onOpenChange) {
			onOpenChange(newOpen);
		} else {
			setIsOpen(newOpen);
		}
	};

	const currentValue = value !== undefined ? value : uncontrolledValue;
	const currentOpen = open !== undefined ? open : isOpen;
	const isDisabled = !!disabled;

	React.useEffect(() => {
		if (isDisabled && currentOpen) {
			handleOpenChange(false);
		}
	}, [isDisabled, currentOpen, handleOpenChange]);

	React.useEffect(() => {
		if (currentOpen) {
			const handleClickOutside = (event: MouseEvent) => {
				const target = event.target as HTMLElement;
				const isClickOnTrigger =
					triggerRef.current && triggerRef.current.contains(target);
				const isClickOnContent = target.closest(
					'[data-select-content]',
				);
				if (!isClickOnTrigger && !isClickOnContent) {
					handleOpenChange(false);
				}
			};

			document.addEventListener('mousedown', handleClickOutside);
			return () =>
				document.removeEventListener('mousedown', handleClickOutside);
		}
	}, [currentOpen, handleOpenChange]);

	return (
		<SelectContext.Provider
			value={{
				value: currentValue,
				onValueChange: handleValueChange,
				open: currentOpen,
				setOpen: handleOpenChange,
				triggerRef,
				disabled: isDisabled,
			}}
		>
			<div
				className={cn('relative w-full', className)}
				aria-disabled={isDisabled || undefined}
				data-select-container
			>
				{children}
			</div>
		</SelectContext.Provider>
	);
};

interface SelectTriggerProps extends React.HTMLAttributes<HTMLDivElement> {
	children: React.ReactNode;
	hideTriggerIcon?: boolean;
	className?: string;
	disabled?: string;
}

const SelectTrigger = React.forwardRef<HTMLDivElement, SelectTriggerProps>(
	({ className, children, hideTriggerIcon, ...props }, ref) => {
		const context = React.useContext(SelectContext);
		if (!context)
			throw new Error('SelectTrigger must be used within a Select');

		const setRef = React.useCallback(
			(node: HTMLDivElement | null) => {
				(
					context.triggerRef as React.MutableRefObject<HTMLDivElement | null>
				).current = node;
				if (typeof ref === 'function') {
					ref(node);
				} else if (ref) {
					(
						ref as React.MutableRefObject<HTMLDivElement | null>
					).current = node;
				}
			},
			[context.triggerRef, ref],
		);

		const triggerDisabled = context.disabled;

		return (
			<div
				ref={setRef}
				className={cn(
					'flex h-10 w-full items-center justify-between rounded-xl',
					'border border-stone-200 bg-white px-3 py-2 text-sm',
					'placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-100',
					'cursor-pointer transition-all [&>span]:line-clamp-1',
					triggerDisabled &&
						'pointer-events-none cursor-not-allowed opacity-50',
					className,
				)}
				onClick={() => {
					if (triggerDisabled) return;
					context.setOpen(!context.open);
				}}
				onKeyDown={(e) => {
					if (triggerDisabled) return;
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						context.setOpen(!context.open);
					}
				}}
				tabIndex={triggerDisabled ? -1 : 0}
				role="combobox"
				aria-expanded={context.open}
				aria-disabled={triggerDisabled || undefined}
				{...props}
			>
				{hideTriggerIcon ? (
					React.Children.count(children) > 1 ? (
						<>
							<div className="min-w-0 flex-1 truncate pr-2 text-left">
								{React.Children.toArray(children).slice(0, -1)}
							</div>
							<div className="shrink-0">
								{React.Children.toArray(children).slice(-1)}
							</div>
						</>
					) : (
						<div className="flex-1 truncate pr-2 text-left">
							{children}
						</div>
					)
				) : (
					<>
						<div className="flex-1 truncate pr-2 text-left">
							{children}
						</div>
						<ChevronDown
							className={cn(
								'h-4 w-4 shrink-0 opacity-50 transition-transform duration-200',
								context.open && 'rotate-180',
							)}
						/>
					</>
				)}
			</div>
		);
	},
);

SelectTrigger.displayName = 'SelectTrigger';

const SelectValue = ({
	children,
	className,
}: {
	children?: React.ReactNode;
	className?: string;
}) => {
	const context = React.useContext(SelectContext);
	if (!context) throw new Error('SelectValue must be used within a Select');

	return <span className={className}>{children}</span>;
};

const SelectContent = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & {
		position?: 'popper' | 'item-aligned';
		/** `max-content`: painel alarga para caber o texto; `trigger` = largura do gatilho (padrão). */
		dropdownWidth?: 'trigger' | 'max-content';
	}
>(
	(
		{
			className,
			children,
			position = 'popper',
			dropdownWidth = 'trigger',
			...props
		},
		ref,
	) => {
		const context = React.useContext(SelectContext);
		const [positionStyle, setPositionStyle] =
			React.useState<React.CSSProperties | null>(null);

		if (!context)
			throw new Error('SelectContent must be used within a Select');

		const updatePosition = React.useCallback(() => {
			const trigger = context.triggerRef.current;
			if (!trigger) return;

			const triggerRect = trigger.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const safetyMargin = 16;
			const defaultMaxHeight = 240;
			const spaceBelow =
				viewportHeight - triggerRect.bottom - safetyMargin;
			const spaceAbove = triggerRect.top - safetyMargin;
			const showAbove =
				spaceBelow < defaultMaxHeight && spaceAbove > spaceBelow;
			const maxAvailableHeight = showAbove
				? Math.min(spaceAbove, defaultMaxHeight)
				: Math.min(spaceBelow, defaultMaxHeight);

			const horizontal =
				dropdownWidth === 'max-content'
					? {
							minWidth: triggerRect.width,
							width: 'max-content' as const,
							maxWidth: `min(100vw - ${
								safetyMargin * 2
							}px, 20rem)`,
					  }
					: { width: triggerRect.width };

			setPositionStyle({
				position: 'fixed',
				left: triggerRect.left,
				/** Acima do ModalDrawer (`z-[10020]`) para listas abrirem dentro de modais. */
				zIndex: 10050,
				maxHeight: maxAvailableHeight,
				overflowY: 'auto',
				...horizontal,
				...(showAbove
					? {
							bottom: viewportHeight - triggerRect.top + 4,
							top: 'auto',
					  }
					: { top: triggerRect.bottom + 4, bottom: 'auto' }),
			});
		}, [context.triggerRef, dropdownWidth]);

		React.useLayoutEffect(() => {
			if (context.open) {
				updatePosition();
			} else {
				setPositionStyle(null);
			}
		}, [context.open, updatePosition]);

		React.useLayoutEffect(() => {
			if (!context.open) return;

			const handle = () => updatePosition();
			window.addEventListener('scroll', handle, true);
			window.addEventListener('resize', handle);
			return () => {
				window.removeEventListener('scroll', handle, true);
				window.removeEventListener('resize', handle);
			};
		}, [context.open, updatePosition]);

		if (!context.open || !positionStyle) return null;

		const content = (
			<div
				ref={ref}
				className={cn(
					`animate-in fade-in-0 zoom-in-95 rounded-lg border border-zinc-200 bg-zinc-50 shadow-lg duration-100
        focus-visible:outline-none focus-visible:ring-1
        [&::-webkit-scrollbar-thumb]:rounded-full
        [&::-webkit-scrollbar-thumb]:bg-gray-300
        hover:[&::-webkit-scrollbar-thumb]:bg-gray-400
        [&::-webkit-scrollbar-track]:bg-transparent
        [&::-webkit-scrollbar]:w-1`,
					className,
				)}
				role="listbox"
				data-select-content
				style={positionStyle}
				{...props}
			>
				<div>
					{React.Children.map(children, (child) => {
						if (React.isValidElement(child)) {
							return React.cloneElement(
								child as React.ReactElement<unknown>,
							);
						}
						return child;
					})}
				</div>
			</div>
		);

		return typeof document !== 'undefined'
			? createPortal(content, document.body)
			: content;
	},
);

SelectContent.displayName = 'SelectContent';

const SelectGroup = ({ children }: { children: React.ReactNode }) => {
	return <div className="space-y-1">{children}</div>;
};

const SelectLabel = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			className={cn(
				'py-1.5 pl-8 pr-2 text-sm font-semibold text-zinc-500',
				className,
			)}
			{...props}
		/>
	);
});
SelectLabel.displayName = 'SelectLabel';

interface SelectItemProps extends React.HTMLAttributes<HTMLDivElement> {
	value: string;
	indicator?: boolean;
	/** Check menor e menos espaço até o texto. */
	compactIndicator?: boolean;
	/** Sem ellipsis — mostra o rótulo inteiro. */
	noTruncate?: boolean;
}

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
	(
		{
			className,
			children,
			value,
			indicator = true,
			compactIndicator = false,
			noTruncate = false,
			...props
		},
		ref,
	) => {
		const context = React.useContext(SelectContext);
		if (!context)
			throw new Error('SelectItem must be used within a Select');

		const isSelected = context.value === value;

		return (
			<div
				ref={ref}
				className={cn(
					'relative flex w-full cursor-pointer select-none items-center rounded-md py-2.5 text-sm outline-none transition-colors hover:bg-stone-200 focus:bg-stone-50',
					indicator && !compactIndicator && 'pl-10 pr-4',
					indicator && compactIndicator && 'pl-6 pr-3',
					!indicator && 'px-4',
					isSelected &&
						'bg-stone-300/50 font-semibold text-stone-900',
					className,
				)}
				onClick={() => {
					context.onValueChange(value);
					context.setOpen(false);
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						context.onValueChange(value);
						context.setOpen(false);
					}
				}}
				tabIndex={0}
				role="option"
				aria-selected={isSelected}
				{...props}
			>
				{indicator && (
					<span
						className={cn(
							'absolute flex items-center justify-center',
							compactIndicator
								? 'left-1.5 h-3 w-3'
								: 'left-2 h-4 w-4',
						)}
					>
						{isSelected && (
							<Check
								className={cn(
									'text-stone-600',
									compactIndicator ? 'h-3 w-3' : 'h-4 w-4',
								)}
								strokeWidth={compactIndicator ? 2 : 2.5}
							/>
						)}
					</span>
				)}
				<span
					className={cn(
						noTruncate
							? 'whitespace-normal break-words pr-0.5'
							: 'truncate',
					)}
				>
					{children}
				</span>
			</div>
		);
	},
);

SelectItem.displayName = 'SelectItem';

const SelectSeparator = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			className={cn('mx-2 my-1 h-px bg-zinc-200', className)}
			{...props}
		/>
	);
});
SelectSeparator.displayName = 'SelectSeparator';

const SelectScrollUpButton = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			className={cn(
				'flex cursor-default items-center justify-center py-1',
				className,
			)}
			{...props}
		>
			<ChevronUp className="h-4 w-4" />
		</div>
	);
});
SelectScrollUpButton.displayName = 'SelectScrollUpButton';

const SelectScrollDownButton = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
	return (
		<div
			ref={ref}
			className={cn(
				'flex cursor-default items-center justify-center py-1',
				className,
			)}
			{...props}
		>
			<ChevronDown className="h-4 w-4" />
		</div>
	);
});
SelectScrollDownButton.displayName = 'SelectScrollDownButton';

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
};
